import React from 'react';
import { StyleProp, ViewStyle, Pressable, LayoutChangeEvent, GestureResponderEvent } from 'react-native';
import { StateStyles, useInteractionStateStyles } from '../../../hooks/useInteractionStateStyles';
import { useIsDisabledByParent } from '../IsDisabledProvider';

export type PressableDynamicOpacityOwnProps = {
    onPress?: (e: GestureResponderEvent) => void;
    children: React.ReactNode;
    pressedOpacity?: number;
    isDisabled?: boolean;
    style?: StyleProp<ViewStyle>;
    testID?: string;
    /**
     * Makes the component pressable. If false, the component will not be pressable.
     *
     * Convenience prop for conditionally rendering within a pressable container.
     */
    makePressable?: boolean;
    addHoverEffect?: boolean;
    onFocus?: () => void;
    onLayout?: (event: LayoutChangeEvent) => void;
    onTouchStart?: () => void;
    onTouchEnd?: () => void;
    customStateStyles?: Partial<StateStyles<ViewStyle>>;
    hoverDetectionContainerStyles?: StyleProp<ViewStyle>;
};

export type PressableDynamicOpacityProps = PressableDynamicOpacityOwnProps;

const PressableDynamicOpacity: React.FC<PressableDynamicOpacityProps> = ({
    onPress,
    children,
    isDisabled,
    style,
    testID,
    makePressable = true,
    addHoverEffect = false,
    customStateStyles,
    hoverDetectionContainerStyles,
    ...toPass
}) => {
    const isDisabledByParent = useIsDisabledByParent(isDisabled);
    const { onPressIn, onPressOut, renderInHoverContainer, styles } = useInteractionStateStyles({
        hoverDetectionContainerStyles,
        isDisabled: isDisabledByParent,
        stateStyles: {
            default: {
                opacity: 1,
                ...(customStateStyles?.default || {}),
            },
            hovered: {
                opacity: 0.8,
                ...(customStateStyles?.hovered || {}),
            },
            pressed: {
                opacity: 0.6,
                ...(customStateStyles?.pressed || {}),
            },
            inactive: {
                opacity: 0.5,
                ...(customStateStyles?.inactive || {}),
            },
        },
    });
    if (!makePressable) return <>{children}</>;

    const mainContent = (
        <Pressable
            onPress={isDisabledByParent ? undefined : onPress}
            onPressIn={onPressIn}
            onPressOut={onPressOut}
            style={[styles, style]}
            testID={testID}
            disabled={isDisabledByParent}
            pointerEvents="auto"
            {...toPass}
        >
            {children}
        </Pressable>
    );

    return addHoverEffect ? renderInHoverContainer(mainContent) : mainContent;
};

export default React.memo(PressableDynamicOpacity);
